import { Stack } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { EOnboardingTaskType } from '@feathr/blackbox';
import { AlertV2, Button, Modal, Superpixel } from '@feathr/components';
import { EAlertType } from '@feathr/components/src/AlertV2/AlertV2';
import { useAccount } from '@feathr/extender/state';

interface ISetupSuperPixelModalProps {
  onClose: () => void;
  onComplete?: (taskType: EOnboardingTaskType) => void;
}

function SetupSuperPixelModal({ onClose }: Readonly<ISetupSuperPixelModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { id } = useAccount();

  return (
    <Modal
      description={t('Install tracking code on your website')}
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button onClick={onClose} type={'primary'}>
            {t('Done')}
          </Button>
        </>
      }
      size={'lg'}
      title={t('Setup up your Super Pixel')}
    >
      <Stack>
        <div>
          <p>
            {t(
              'Track website visitors by adding our tracking code to your site. Copy the snippet below and place it in the <head> of your website.',
            )}
          </p>
        </div>
        <Superpixel.Well accountId={id} />
        <AlertV2
          description={[
            t('Place the code as high in the <head> as possible'),
            t('Add to all pages you want to track'),
            t('Do not place within an iframe'),
            t('The pixel should show as active within 24 hours'),
            t(
              'Contact your internal IT contact if you need technical assistance, or contact Feathr Support below',
            ),
          ]}
          title={t('Important tips')}
          type={EAlertType.info}
        >
          <a
            // TODO: Update link to the correct documentation. We have only used GTM for like 5 years and we have no documentation about direct pixel placement anymore
            href={
              'https://help.feathr.co/hc/en-us/articles/32550331807639-How-to-Add-the-Super-Pixel-to-your-Pages'
            }
            target={'_blank'}
          >
            {t('Read our documentation')}
          </a>
          <a
            href={'mailto:support@feathr.co?subject=Help with Super Pixel placement'}
            target={'_blank'}
          >
            {t('Contact support')}
          </a>
        </AlertV2>
      </Stack>
    </Modal>
  );
}

export default SetupSuperPixelModal;
